import { StockName as StockLabel } from '@/components/stock-description';
/**
 * ⌘K 命令面板（规格 §5.2）：去往页面 + 板块 + 标的，一个入口。
 * 取代旧顶栏的「搜索股票或板块」下拉 —— 那是手写的 div + 全局 mousedown 关闭。
 *
 * 过滤自己做（shouldFilter=false）：页面组在本地按名字/拼音/首字母匹配、
 * 不等网络；股票与板块是服务端结果，不能再被 cmdk 的模糊匹配二次筛掉。
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Title as DialogTitle, Description as DialogDescription } from '@radix-ui/react-dialog';
import { CornerDownLeft, Layers, Loader2, Search } from 'lucide-react';
import { useUniversalSearch, type UniversalSectorHit } from '@/hooks';
import { addRecentSearch } from '@/lib/utils/recent-searches';
import { isStockCodeFormat, normalizeSymbol } from '@/lib/utils/symbol';
import { useSymbolSearch } from '@/features/drawing-desk/api';
import { NAV_ROUTES, ROUTES, SEGMENT_LABEL, type RouteEntry } from './registry';
import { useShellStore } from './shell-store';

const ASSET_LABEL = { index: '指数', etf: 'ETF', industry: '同花顺行业', concept: '同花顺概念', hk_stock: '港股', hk_index: '港股指数', us_stock:'美股', us_index:'美股指数', us_etf:'美股ETF', kr_stock:'韩股' };
const SEARCH_LABEL = '搜索股票、行业、概念、ETF、指数或页面';

const SECTOR_TYPE_LABEL: Record<string, string> = { industry: '行业', concept: '概念', region: '地域' };

/** 可以从面板直接去的页面：目录里的全部 + 不带路径参数的隐藏页 */
const GOTO_ROUTES = [
  ...NAV_ROUTES,
  ...ROUTES.filter((r) => !r.segment && !r.path.includes(':')),
];

export function matchPage(r: RouteEntry, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (r.name.toLowerCase().includes(needle)) return true;
  return r.keywords.some((k) => k.toLowerCase().includes(needle));
}

function sectorHref(s: UniversalSectorHit): string {
  const params = new URLSearchParams({
    sector: s.sector_name,
    type: s.sector_type === 'industry' ? 'industry' : 'concept',
  });
  return `/rotation-desk?${params.toString()}`;
}

const ITEM =
  'flex h-9 cursor-default select-none items-center gap-3 rounded-md px-3 text-body text-ink-2 ' +
  'data-[selected=true]:bg-paper-2 data-[selected=true]:text-ink-1';
const GROUP =
  'px-2 pb-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 ' +
  '[&_[cmdk-group-heading]]:text-caps [&_[cmdk-group-heading]]:text-ink-3';

export function CommandPalette() {
  const open = useShellStore((s) => s.paletteOpen);
  const setOpen = useShellStore((s) => s.setPaletteOpen);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(t);
  }, [query]);
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // 与画线工作台使用同一份已验证行情目录，保留 ths: / sh / sz 身份。
  const ready = !!query.trim() && query.trim() === debounced;
  const assets = useSymbolSearch(debounced, open && debounced.length >= 2);
  const legacy = useUniversalSearch(open ? debounced : '');
  const instruments = ready ? assets.data?.items ?? [] : [];
  // 等待行情候选完成再挂载旧板块，避免 cmdk 将先返回的旧入口锁为回车目标。
  const sectors = ready && !assets.isFetching ? legacy.data?.sectors ?? [] : [];
  const isFetching = assets.isFetching || legacy.isFetching || (!!query.trim() && !ready);
  // 已有精确代码候选时直接选择候选；六位代码可能同时属于股票和指数，不能猜。
  const rawCode = query.trim();
  const hasCodeHit = instruments.some(s => s.symbol.replace(/^ths:|^(sh|sz)/i, '').replace(/\.(SH|SZ|BJ)$/i, '').toLowerCase() === rawCode.toLowerCase());
  const codeTarget = /^(sh|sz)\d{6}$/i.test(rawCode) ? rawCode.toLowerCase() : normalizeSymbol(rawCode);
  const pages = useMemo(() => GOTO_ROUTES.filter((r) => matchPage(r, query)), [query]);
  const codeLike = rawCode.length > 0 && isStockCodeFormat(rawCode);
  const directCode = codeLike && ready && assets.isSuccess && !hasCodeHit;

  const go = (to: string, state?: Record<string, unknown>) => {
    setOpen(false);
    navigate(to, state ? { state } : undefined);
  };
  const goInstrument = (symbol: string, name?: string) => {
    addRecentSearch(symbol, name);
    go(`/drawing-desk?${new URLSearchParams({ symbol })}`);
  };

  const pageGroup = pages.length > 0 && (
    <Command.Group heading="去往" className={GROUP}>
      {pages.map((r) => (
        <Command.Item key={r.path} value={`page:${r.path}`} onSelect={() => go(r.path)} className={ITEM}>
          <span className="flex-1 truncate">{r.name}</span>
          {r.segment && <span className="text-caps text-ink-3">{SEGMENT_LABEL[r.segment]}</span>}
        </Command.Item>
      ))}
    </Command.Group>
  );

  const instrumentGroup = (directCode || instruments.length > 0) && (
    <Command.Group heading="行情标的" className={GROUP}>
      {directCode && (
        <Command.Item
          value={`code:${query}`}
          onSelect={() => goInstrument(codeTarget)}
          className={ITEM}
        >
          <span className="flex-1">打开 <span className="font-mono tabular-nums">{codeTarget}</span></span>
          <CornerDownLeft className="h-3.5 w-3.5 text-ink-3" />
        </Command.Item>
      )}
      {instruments.map((s) => (
        <Command.Item key={s.symbol} value={`asset:${s.symbol}`} onSelect={() => goInstrument(s.symbol, s.name)} className={ITEM}>
          <span className="truncate font-semibold text-ink-1"><StockLabel symbol={s?.symbol} name={s.name} /></span>
          <span className="font-mono text-code tabular-nums text-ink-3">{s.symbol}</span>
          <span className="ml-auto shrink-0 text-caps text-ink-3">{s.kind ? ASSET_LABEL[s.kind] : '股票'}{s.delisted ? ' · 非主档' : ''}</span>
        </Command.Item>
      ))}
    </Command.Group>
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label={SEARCH_LABEL}
      shouldFilter={false}
      overlayClassName="fixed inset-0 z-50 bg-black/30"
      contentClassName="fixed left-1/2 top-[12vh] z-50 w-[min(640px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-lg border border-rule bg-paper shadow-2xl"
    >
      <DialogTitle className="sr-only">{SEARCH_LABEL}</DialogTitle>
      <DialogDescription className="sr-only">输入代码或名称搜索，使用方向键选择，回车打开，行情标的默认在画线工作台打开，Esc 关闭。</DialogDescription>
      <div className="flex items-center gap-2 border-b border-rule px-4">
        <Search className="h-4 w-4 shrink-0 text-ink-3" />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="股票、行业、概念、ETF、指数 / 代码或名称"
          className="h-11 flex-1 bg-transparent text-heading text-ink-1 outline-none placeholder:text-ink-3"
        />
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-ink-3" />}
      </div>
      {ready && assets.isError && <p role="alert" className="px-4 py-2 text-body text-ink-3">行情标的搜索失败，请重试；下方仍可打开页面或板块轮动。</p>}
      <Command.List className="max-h-[60vh] overflow-y-auto py-1">
        <Command.Empty className="py-8 text-center text-body text-ink-3">
          {isFetching ? '搜索中…' : assets.isError ? '行情标的搜索失败，请重试' : '没有匹配的页面、板块或行情标的'}
        </Command.Empty>
        {/* 输入代码时标的组置顶；其余情况页面组在前（本地结果，不等网络） */}
        {codeLike ? instrumentGroup : pageGroup}
        {!codeLike && instrumentGroup}
        {sectors.length > 0 && (
          <Command.Group heading="板块轮动" className={GROUP}>
            {sectors.map((s) => (
              <Command.Item
                key={`${s.sector_type}-${s.sector_name}`}
                value={`sector:${s.sector_type}:${s.sector_name}`}
                onSelect={() => go(sectorHref(s))}
                className={ITEM}
              >
                <Layers className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                <span className="truncate font-semibold text-ink-1">{s.sector_name}</span>
                <span className="text-caps text-ink-3">{SECTOR_TYPE_LABEL[s.sector_type] ?? s.sector_type}</span>
                <span className="ml-auto font-mono text-data tabular-nums text-ink-3">{s.member_count} 只</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
        {codeLike && pageGroup}
      </Command.List>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-rule px-4 py-2 text-caps text-ink-3">
        <span><kbd className="font-mono">↑↓</kbd> 选择</span>
        <span><kbd className="font-mono">↵</kbd> 打开</span>
        <span>行情标的 → 画线工作台</span>
        <span><kbd className="font-mono">Esc</kbd> 关闭</span>
      </div>
    </Command.Dialog>
  );
}
